import type { Editor } from '@tiptap/core';
import * as React from 'react';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';

// Mirrors the server's RequestSizeLimit on POST attachment/image (decimal 20 MB per request);
// each image uploads in its own request so the limit applies per file.
export const INLINE_IMAGE_BYTES_MAX = 20_000_000;

// The box an inserted image is scaled to fit, matching the email composer's inline images. The
// sanitizer drops `style`, so the width attribute is the only sizing that reaches the recipient —
// unsized, their client falls back to the image's natural pixel width.
const IMAGE_EMAIL_WIDTH_MAX_PX = 600;
const IMAGE_EMAIL_HEIGHT_MAX_PX = 400;

// One scale factor for both axes so the aspect ratio holds; the node renders height automatically.
const scaledEmailWidth = (naturalWidth: number, naturalHeight: number): number | null => {
	if (naturalWidth <= 0 || naturalHeight <= 0) return null;
	const scale = Math.min(1, IMAGE_EMAIL_WIDTH_MAX_PX / naturalWidth, IMAGE_EMAIL_HEIGHT_MAX_PX / naturalHeight);
	return Math.round(naturalWidth * scale);
};

// The probe owns its object URL so the upload finishing cannot revoke it mid-decode. Null on an
// undecodable file leaves the image unsized rather than blocking the send.
const measureEmailWidth = (file: File): Promise<number | null> =>
	new Promise(resolve => {
		const url = URL.createObjectURL(file);
		const probe = new Image();
		const settle = (width: number | null) => {
			URL.revokeObjectURL(url);
			resolve(width);
		};
		probe.onload = () => settle(scaledEmailWidth(probe.naturalWidth, probe.naturalHeight));
		probe.onerror = () => settle(null);
		probe.src = url;
	});

const findImagePos = (editor: Editor, src: string): number | null => {
	let found: number | null = null;
	editor.state.doc.descendants((node, pos) => {
		if (found != null) return false;
		if (node.type.name === 'image' && node.attrs.src === src) {
			found = pos;
			return false;
		}
		return true;
	});
	return found;
};

const replaceImageSrc = (editor: Editor, placeholderUrl: string, hostedUrl: string) => {
	const pos = findImagePos(editor, placeholderUrl);
	if (pos == null) return;
	const node = editor.state.doc.nodeAt(pos);
	if (!node) return;
	editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: hostedUrl }));
};

const setImageWidth = (editor: Editor, src: string, width: number) => {
	const pos = findImagePos(editor, src);
	if (pos == null) return;
	const node = editor.state.doc.nodeAt(pos);
	if (!node || node.attrs.width) return;
	editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, width }));
};

const removeImage = (editor: Editor, placeholderUrl: string) => {
	const pos = findImagePos(editor, placeholderUrl);
	if (pos == null) return;
	const node = editor.state.doc.nodeAt(pos);
	if (!node) return;
	editor.view.dispatch(editor.state.tr.delete(pos, pos + node.nodeSize));
};

/**
 * Inline images for the composer: each pasted/dropped image is inserted immediately with a local
 * blob URL as a visible placeholder, uploaded to attachment/image, then the placeholder src is
 * swapped for the hosted public URL (or the node is removed and an error surfaced on failure).
 * Sends stay disabled while any upload is in flight so a blob URL can never leave the editor.
 */
export const useComposerInlineImages = (editor: Editor | null) => {
	const [uploadingImageCount, setUploadingImageCount] = React.useState(0);
	// Support has no MutationCache/toast, so failed uploads must surface inline (same rationale as
	// useComposerAttachments); per-upload callbacks handle the rest.
	const [imageUploadError, setImageUploadError] = React.useState<string | null>(null);
	const uploadImage = useImageAttachmentMutation();

	const insertImages = (files: File[], insertAt?: number) => {
		if (!editor) return;
		setImageUploadError(null);

		const accepted: { file: File; placeholderUrl: string; currentSrc: string }[] = [];
		for (const file of files) {
			if (!file.type.startsWith('image/')) continue;
			if (file.size > INLINE_IMAGE_BYTES_MAX) {
				setImageUploadError(`"${file.name}" is larger than the 20 MB image limit.`);
				continue;
			}
			const placeholderUrl = URL.createObjectURL(file);
			accepted.push({ file, placeholderUrl, currentSrc: placeholderUrl });
		}
		if (accepted.length === 0) return;

		const imageNodes = accepted.map(({ file, placeholderUrl }) => ({
			type: 'image',
			attrs: { src: placeholderUrl, alt: file.name },
		}));
		if (insertAt != null) {
			editor.chain().focus().insertContentAt(insertAt, imageNodes).run();
		} else {
			editor.chain().focus().insertContent(imageNodes).run();
		}

		// After the insert, not before: awaiting the decode would let `insertAt` — a position captured
		// from the drop — go stale. Tracked by `currentSrc` so it lands even if the upload swapped first.
		for (const image of accepted) {
			measureEmailWidth(image.file).then(width => {
				if (width == null || editor.isDestroyed) return;
				setImageWidth(editor, image.currentSrc, width);
			});
		}

		setUploadingImageCount(count => count + accepted.length);
		accepted.forEach(image => {
			const { file, placeholderUrl } = image;
			const finishUpload = (apply: (activeEditor: Editor) => void) => {
				if (!editor.isDestroyed) apply(editor);
				URL.revokeObjectURL(placeholderUrl);
				setUploadingImageCount(count => count - 1);
			};
			uploadImage.mutate(
				{ files: [file] },
				{
					onSuccess: attachments => {
						const hostedUrl = attachments[0]?.url;
						finishUpload(activeEditor => {
							if (hostedUrl) {
								replaceImageSrc(activeEditor, placeholderUrl, hostedUrl);
								image.currentSrc = hostedUrl;
							} else {
								removeImage(activeEditor, placeholderUrl);
								setImageUploadError(`Failed to upload "${file.name}". Please try again.`);
							}
						});
					},
					onError: error => {
						finishUpload(activeEditor => {
							removeImage(activeEditor, placeholderUrl);
							setImageUploadError(getApiErrorMessage(error, `Failed to upload "${file.name}". Please try again.`));
						});
					},
				}
			);
		});
	};

	const dismissImageUploadError = () => setImageUploadError(null);

	return {
		dismissImageUploadError,
		hasUploadingImages: uploadingImageCount > 0,
		imageUploadError,
		insertImages,
	};
};
